import type {
    ThemeCssSize,
    ThemeFont,
    ThemeResponsive,
    ThemeSelectedFonts,
} from '@sites/site-theme/src/entity/schemas';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';

import type { ThemeBreakpoint } from './responsive';
import { resolveBreakpoint, THEME_BREAKPOINTS } from './responsive';

/**
 * The seven typographic properties a theme font carries.
 *
 * Derived from the enum rather than written out a second time. The two lists sat in different
 * folders saying the same seven strings, and the enum is the one the CSS-variable tables are keyed
 * by, so it wins.
 */
export type ThemeFontProperty = `${ThemeFontProperties}`;

export const THEME_FONT_PROPERTIES: ThemeFontProperty[] = Object.values(ThemeFontProperties);

/** `{ value: 16, unit: 'px' }` → `'16px'`. */
export function cssSizeToString(size: ThemeCssSize): string {
    return `${size.value}${size.unit}`;
}

/**
 * One property of one font at one breakpoint, as the string a CSS variable takes — or `undefined`
 * when neither that breakpoint nor any it inherits from has a value.
 *
 * **With the inheritance `resolveBreakpoint` applies** (mobile → tablet → desktop). The stylesheet
 * reads `--h1-font-size-tablet` with no fallback of its own, so a tablet variable that is absent
 * does not let the desktop rule stand: the declaration is invalid and the property is inherited
 * from the parent instead. A theme stores only what a breakpoint overrides — a synchronised one
 * stores desktop alone — so the variables are what spell every breakpoint out. They are rebuilt on
 * every theme change, so a resolved tablet value follows a later desktop edit.
 */
export function formatThemeFontProperty(
    font: ThemeFont,
    property: ThemeFontProperty,
    breakpoint: ThemeBreakpoint
): string | undefined {
    const value = resolveBreakpoint(font[property] as ThemeResponsive<unknown> | undefined, breakpoint);

    if (value === undefined || value === null) {
        return undefined;
    }

    if (typeof value === 'object' && 'value' in value && 'unit' in value) {
        return cssSizeToString(value as ThemeCssSize);
    }

    return String(value);
}

/**
 * Every property of one font at one breakpoint, keyed by property name — the lookup table the
 * button formatters read when a button takes its typography from the `button` tag's font.
 */
export function formatThemeFont(font: ThemeFont, breakpoint: ThemeBreakpoint): Record<string, string> {
    const formatted: Record<string, string> = {};

    THEME_FONT_PROPERTIES.forEach((property) => {
        const value = formatThemeFontProperty(font, property, breakpoint);

        if (value !== undefined) {
            formatted[property] = value;
        }
    });

    return formatted;
}

/** Family and weight cannot differ per device — the panel writes them to all three at once. */
const ALWAYS_SHARED_FONT_PROPERTIES: readonly ThemeFontProperty[] = ['fontFamily', 'fontWeight'];

function sameFontValue(a: unknown, b: unknown): boolean {
    return a === b || (a !== undefined && b !== undefined && JSON.stringify(a) === JSON.stringify(b));
}

/** Drops the tablet and mobile values that equal what they would inherit anyway. */
function compactResponsive<T>(responsive: ThemeResponsive<T> | undefined): ThemeResponsive<T> | undefined {
    if (!responsive) {
        return responsive;
    }

    const { tablet, mobile, ...rest } = responsive;
    const dropTablet = tablet !== undefined && sameFontValue(tablet, responsive.desktop);
    const dropMobile = mobile !== undefined && sameFontValue(mobile, tablet ?? responsive.desktop);

    if (!dropTablet && !dropMobile) {
        return responsive;
    }

    return {
        ...rest,
        ...(tablet === undefined || dropTablet ? {} : { tablet }),
        ...(mobile === undefined || dropMobile ? {} : { mobile }),
    };
}

/**
 * Stores only what a breakpoint overrides.
 *
 * A synchronised font keeps desktop alone, since tablet and mobile resolve to it anyway. A
 * desynchronised one keeps its per-device values even where they happen to match: they are
 * overrides the user set, and a later desktop edit must not carry into them. Family and weight
 * are compacted either way, because they are never per-device.
 *
 * Returns the same object when nothing was dropped.
 */
export function compactThemeFont(font: ThemeFont, syncWithDesktop: boolean): ThemeFont {
    let next: ThemeFont | undefined;

    THEME_FONT_PROPERTIES.forEach((property) => {
        if (!syncWithDesktop && !ALWAYS_SHARED_FONT_PROPERTIES.includes(property)) {
            return;
        }

        const stored = font[property] as ThemeResponsive<unknown> | undefined;
        const compacted = compactResponsive(stored);

        if (compacted !== stored) {
            next ??= { ...font };
            (next as Record<string, unknown>)[property] = compacted;
        }
    });

    return next ?? font;
}

export function compactThemeFonts(fonts: ThemeSelectedFonts): ThemeSelectedFonts {
    const syncWithDesktop = fonts.syncWithDesktop ?? true;
    const selected = fonts.selected.map((font) => compactThemeFont(font, syncWithDesktop));

    return selected.every((font, index) => font === fonts.selected[index]) ? fonts : { ...fonts, selected };
}

/**
 * Writes every breakpoint out with the value it resolves to — the inverse of `compactThemeFont`.
 *
 * Needed before a per-device override lands on a synchronised font: mobile inherits from tablet,
 * so a tablet override on a font that stores desktop alone would carry into mobile too.
 */
export function expandThemeFont(font: ThemeFont): ThemeFont {
    const next = { ...font };

    THEME_FONT_PROPERTIES.forEach((property) => {
        const stored = font[property] as ThemeResponsive<unknown> | undefined;

        if (stored) {
            const expanded: ThemeResponsive<unknown> = {};

            THEME_BREAKPOINTS.forEach((breakpoint) => {
                const value = resolveBreakpoint(stored, breakpoint);

                if (value !== undefined) {
                    expanded[breakpoint] = value;
                }
            });

            (next as Record<string, unknown>)[property] = expanded;
        }
    });

    return next;
}
